import type { CompletedLap, DataCoverage, DriverInfo, RaceRecording, RaceSnapshot, TrackStatus } from './race';
import { numericGap } from './insights';

export interface RankObservation { atMs: number; position: number; lap: number; breakBefore: boolean }
export interface PaceObservation { lap: number; atMs: number; duration: number; delta: number }
export interface VisualDriver extends DriverInfo { ranks: RankObservation[]; pace: PaceObservation[]; dashed: boolean }
export interface RaceVisuals {
  startMs: number; endMs: number; firstMs: number; lastLap: number; drivers: VisualDriver[];
  flags: { fromMs: number; toMs: number; status: TrackStatus }[];
  /** The latest source time allowed to contribute visible evidence. The axis can extend beyond it. */
  evidenceEndMs?: number;
}
export interface RaceVisualOptions { evidenceEndMs?: number }
const neutralized = (status: TrackStatus) => status === 'RED' || status === 'SC' || status === 'VSC';
const overlaps = (a: number, b: number, c: number, d: number) => a < d && b > c;
const isUnloadedGap = (reason: string) => /未载入|尚未载入|loading|unloaded/i.test(reason);
const gapCrossed = (coverage: DataCoverage | undefined, fromMs: number, toMs: number) => coverage?.gaps.some(gap => !isUnloadedGap(gap.reason) && overlaps(fromMs, toMs, gap.fromMs, gap.toMs)) ?? false;

/** The timeline is the recording domain, never the currently inspected snapshot. */
export function raceVisuals(recording: RaceRecording | undefined, snapshot: RaceSnapshot, options: RaceVisualOptions = {}): RaceVisuals {
  const coverage = recording?.coverage ?? snapshot.coverage;
  const endMs = Math.max(1, recording?.durationMs ?? coverage?.endMs ?? snapshot.atMs);
  const evidenceEndMs = Math.max(0, Math.min(endMs, Number.isFinite(options.evidenceEndMs) ? options.evidenceEndMs! : endMs));
  const events = [...new Map((recording?.events ?? []).map(event => [event.id, event])).values()]
    .filter(event => Number.isFinite(event.atMs) && event.atMs >= 0 && event.atMs <= evidenceEndMs).sort((a, b) => a.atMs - b.atMs);
  const seedStart = events.filter(event => event.type === 'timing' && /^seed:timing:/.test(event.id)).map(event => event.atMs);
  const firstMs = Math.min(evidenceEndMs, Math.max(0, coverage?.startMs ?? 0, seedStart.length ? Math.min(...seedStart) : 0));
  const infos = recording?.drivers.length ? recording.drivers : snapshot.drivers;
  const drivers: VisualDriver[] = [...infos].sort((a, b) => a.number - b.number).map((driver, index, all) => ({
    ...driver, ranks: [], pace: [], dashed: all.slice(0, index).some(other => other.team === driver.team),
  }));
  const byDriver = new Map(drivers.map(driver => [driver.number, driver]));
  const laps = new Map<number, Map<number, CompletedLap>>();
  const currentLap = new Map<number, number>();
  const pitLaps = new Map<number, Set<number>>();
  const pitOpen = new Map<number, number>();
  const pitWindows = new Map<number, { fromMs: number; toMs: number }[]>();
  const flags: RaceVisuals['flags'] = [];
  let previousFlag: { atMs: number; status: TrackStatus } | null = null;
  const sourceGaps = (coverage?.gaps ?? []).filter(gap => !isUnloadedGap(gap.reason) && gap.toMs > gap.fromMs && gap.fromMs < evidenceEndMs && gap.toMs > 0)
    .map(gap => ({ fromMs: Math.max(0, gap.fromMs), toMs: Math.min(evidenceEndMs, gap.toMs) })).sort((a, b) => a.fromMs - b.fromMs);
  const gapBetween = (fromMs: number, toMs: number) => sourceGaps.find(gap => gap.fromMs > fromMs && gap.fromMs < toMs);
  const markPitLap = (number: number, lap: number) => {
    if (!Number.isInteger(lap) || lap < 1) return;
    const set = pitLaps.get(number) ?? new Set<number>(); set.add(lap); set.add(lap + 1); pitLaps.set(number, set);
  };
  for (const event of events) {
    if (event.type === 'status') {
      if (sourceGaps.some(gap => event.atMs >= gap.fromMs && event.atMs < gap.toMs)) continue;
      const gap = previousFlag && gapBetween(previousFlag.atMs, event.atMs);
      if (previousFlag && gap) {
        if (gap.fromMs > previousFlag.atMs) flags.push({ fromMs: previousFlag.atMs, toMs: gap.fromMs, status: previousFlag.status });
        flags.push({ fromMs: gap.fromMs, toMs: event.atMs, status: 'UNKNOWN' });
        previousFlag = { atMs: event.atMs, status: event.status };
      } else if (previousFlag && previousFlag.status !== event.status) {
        flags.push({ fromMs: previousFlag.atMs, toMs: event.atMs, status: previousFlag.status });
        previousFlag = { atMs: event.atMs, status: event.status };
      } else if (!previousFlag) previousFlag = { atMs: event.atMs, status: event.status };
    }
    if (event.type === 'pit') markPitLap(event.stop.driverNumber, event.stop.lap);
    if (event.type !== 'timing') continue;
    const number = event.driverNumber, driver = byDriver.get(number);
    if (!driver) continue;
    const lap = event.patch.lap ?? currentLap.get(number) ?? 0;
    currentLap.set(number, lap);
    const position = event.patch.position;
    if (typeof position === 'number' && position > 0 && Number.isFinite(position) && event.atMs >= firstMs) {
      const previous = driver.ranks.at(-1);
      const broken = Boolean(previous && gapCrossed(coverage, previous.atMs, event.atMs));
      if (!previous || previous.position !== position || broken) driver.ranks.push({ atMs: event.atMs, position, lap, breakBefore: broken });
    }
    if (event.patch.status === 'PIT') { if (!pitOpen.has(number)) pitOpen.set(number, event.atMs); markPitLap(number, lap); }
    else if (event.patch.status && pitOpen.has(number)) {
      const windows = pitWindows.get(number) ?? [];
      windows.push({ fromMs: pitOpen.get(number)!, toMs: event.atMs }); pitWindows.set(number, windows); pitOpen.delete(number);
    }
    if (event.patch.recentLaps) {
      const known = laps.get(number) ?? new Map<number, CompletedLap>();
      for (const completed of event.patch.recentLaps) if (completed.atMs <= event.atMs) known.set(completed.lap, completed);
      laps.set(number, known);
    }
  }
  if (previousFlag) {
    const gap = sourceGaps.find(candidate => candidate.fromMs > previousFlag!.atMs);
    if (gap) {
      if (gap.fromMs > previousFlag.atMs) flags.push({ fromMs: previousFlag.atMs, toMs: gap.fromMs, status: previousFlag.status });
      flags.push({ fromMs: gap.fromMs, toMs: evidenceEndMs, status: 'UNKNOWN' });
    } else if (evidenceEndMs > previousFlag.atMs) flags.push({ fromMs: previousFlag.atMs, toMs: evidenceEndMs, status: previousFlag.status });
  }
  for (const [number, fromMs] of pitOpen) { const windows = pitWindows.get(number) ?? []; windows.push({ fromMs, toMs: endMs }); pitWindows.set(number, windows); }
  // Snapshot laps can extend a rolling stream, but never replace a newer event correction.
  for (const driver of snapshot.drivers) {
    const known = laps.get(driver.number) ?? new Map<number, CompletedLap>();
    for (const lap of driver.recentLaps ?? []) if (!known.has(lap.lap) && lap.atMs <= evidenceEndMs) known.set(lap.lap, lap);
    laps.set(driver.number, known);
  }
  const valid = new Map<number, CompletedLap[]>();
  const sameLap = new Map<number, number[]>();
  for (const [number, known] of laps) {
    const clean = [...known.values()].filter(lap => {
      if (lap.deleted || lap.partial || !lap.duration || !Number.isFinite(lap.duration) || lap.duration <= 0 || lap.atMs > evidenceEndMs) return false;
      const start = lap.atMs - lap.duration * 1000;
      return start >= firstMs && !pitLaps.get(number)?.has(lap.lap) && !gapCrossed(coverage, start, lap.atMs)
        && !pitWindows.get(number)?.some(window => overlaps(start, lap.atMs, window.fromMs, window.toMs))
        && !flags.some(flag => neutralized(flag.status) && overlaps(start, lap.atMs, flag.fromMs, flag.toMs));
    });
    valid.set(number, clean);
    for (const lap of clean) { const values = sameLap.get(lap.lap) ?? []; values.push(lap.duration!); sameLap.set(lap.lap, values); }
  }
  for (const driver of drivers) {
    driver.pace = (valid.get(driver.number) ?? []).flatMap(lap => {
      const peers = sameLap.get(lap.lap)!;
      return peers.length < 2 ? [] : [{ lap: lap.lap, atMs: lap.atMs, duration: lap.duration!, delta: Math.max(0, lap.duration! - Math.min(...peers)) }];
    }).sort((a, b) => a.lap - b.lap);
    const last = driver.ranks.at(-1);
    if (last && last.atMs < evidenceEndMs) {
      const nextGap = coverage?.gaps.filter(gap => !isUnloadedGap(gap.reason) && gap.fromMs >= last.atMs).sort((a, b) => a.fromMs - b.fromMs)[0];
      const toMs = Math.min(evidenceEndMs, nextGap?.fromMs ?? evidenceEndMs);
      if (toMs > last.atMs) driver.ranks.push({ ...last, atMs: toMs, breakBefore: false });
    }
  }
  return { startMs: 0, endMs, firstMs, evidenceEndMs, lastLap: Math.max(1, snapshot.lap, ...drivers.flatMap(driver => [...driver.ranks.map(rank => rank.lap), ...driver.pace.map(lap => lap.lap)])), drivers, flags };
}

/**
 * Reveal the evidence in a previously projected model up to one display time.
 *
 * `raceVisuals` does the expensive event reduction and can therefore stay
 * cached while a playback cursor moves. This helper only clips the already
 * reduced observations and adds the current rank hold. It never creates a
 * point beyond the supplied cursor or across a known coverage gap.
 */
export function revealRaceVisuals(model: RaceVisuals, atMs: number, coverage?: DataCoverage): RaceVisuals {
  const boundary = Math.max(0, Math.min(model.evidenceEndMs ?? model.endMs, Number.isFinite(atMs) ? atMs : 0));
  const visibleGaps = coverage?.gaps ?? [];
  const drivers = model.drivers.map(driver => {
    const ranks = driver.ranks.filter(point => point.atMs <= boundary).map(point => ({ ...point }));
    const last = ranks.at(-1);
    if (last && last.atMs < boundary) {
      const gap = visibleGaps.filter(item => !isUnloadedGap(item.reason) && item.fromMs >= last.atMs && item.fromMs < boundary).sort((a, b) => a.fromMs - b.fromMs)[0];
      const toMs = Math.min(boundary, gap?.fromMs ?? boundary);
      if (toMs > last.atMs) ranks.push({ ...last, atMs: toMs, breakBefore: false });
    }
    return { ...driver, ranks, pace: driver.pace.filter(point => point.atMs <= boundary) };
  });
  const flags = model.flags.flatMap(flag => {
    if (flag.fromMs >= boundary) return [];
    const toMs = Math.min(flag.toMs, boundary);
    return toMs > flag.fromMs ? [{ ...flag, toMs }] : [];
  });
  return { ...model, evidenceEndMs: boundary, drivers, flags };
}

export interface ChaseCar { number: number; position: number; seconds: number }
/** A completed-lap counter briefly differs while the field crosses the line.
 * Keep the source's last numeric gap until another observation changes it.
 * A gap is not a countdown to an inferred crossing: sparse/late counter
 * updates must not make the observed point disappear between source events.
 */
function comparableChaseLap(driver: RaceSnapshot['drivers'][number], leader: RaceSnapshot['drivers'][number], snapshot: RaceSnapshot, seconds: number) {
  const difference = (leader.completedLaps ?? leader.lap) - (driver.completedLaps ?? driver.lap);
  if (difference === 0) return true;
  if (difference !== 1 || snapshot.trackStatus === 'FINISHED' || driver.completedLaps == null || leader.completedLaps == null) return false;
  const crossing = leader.recentLaps?.find(lap => lap.lap === leader.completedLaps && !lap.deleted);
  if (!crossing || crossing.atMs > snapshot.atMs || crossing.duration == null || !Number.isFinite(crossing.duration) || crossing.duration <= 0) return false;
  // A whole-lap numeric deficit must not be rendered as a same-lap chase.
  if (seconds >= crossing.duration) return false;
  return true;
}
/** A time-gap plot requires an explicit numeric interval and gap; lapped/unknown values stay absent. */
export function chaseCars(snapshot: RaceSnapshot): ChaseCar[] {
  if (!/race|sprint|正赛|冲刺/i.test(snapshot.session.sessionName) || /qual|shootout|排位/i.test(snapshot.session.sessionName)) return [];
  const field = snapshot.drivers.filter(driver => driver.position !== null).sort((a, b) => a.position! - b.position!);
  const leader = field.find(driver => driver.position === 1);
  if (!leader || snapshot.coverage?.gaps.some(gap => !isUnloadedGap(gap.reason) && snapshot.atMs >= gap.fromMs && snapshot.atMs <= gap.toMs)) return [];
  const cars: ChaseCar[] = [];
  for (const driver of field) {
    if (driver.status !== 'RUNNING') continue;
    const seconds = driver.number === leader.number ? 0 : numericGap(driver.gapToLeader);
    if (seconds === null || driver.number !== leader.number && numericGap(driver.interval) === null) continue;
    if (!comparableChaseLap(driver, leader, snapshot, seconds)) continue;
    cars.push({ number: driver.number, position: driver.position!, seconds });
  }
  return cars.length >= 2 ? cars : [];
}

export function positionAt(driver: VisualDriver, atMs: number, coverage?: DataCoverage): number | null {
  if (coverage?.gaps.some(gap => !isUnloadedGap(gap.reason) && atMs >= gap.fromMs && atMs < gap.toMs)) return null;
  let known: RankObservation | undefined;
  for (const point of driver.ranks) { if (point.atMs > atMs) break; known = point; }
  if (!known || gapCrossed(coverage, known.atMs, atMs)) return null;
  return known.position;
}

export function nearestPace(driver: VisualDriver | undefined, atMs: number): PaceObservation | undefined {
  return driver?.pace.reduce<PaceObservation | undefined>((best, lap) => lap.atMs <= atMs && (!best || lap.atMs > best.atMs) ? lap : best, undefined);
}
